import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import { Table, Button, Section, Form } from 'react-bulma-components';
import { useMsal } from "@azure/msal-react";
import DeleteConfirmationComponent from './admin.delete-confirmation.modal';
import useAxiosWithToken from '../../../hooks/axiosTokenHook';
import { formatDateUI, formatDateISO, getMaxDate } from '../../../utils/utils';
import AdditionalConfirmationModal from './admin.additional-confirmation.modal'
import { debounce } from 'lodash';
import { useMsGraphTokenContext } from '../../../contexts/MsGraphTokenContextProvider';
import { DateRangePicker } from 'react-date-range';

const AdminBookingOverviewComponent = () => {
  const axios = useAxiosWithToken();
  const { msGraphToken, refreshToken } = useMsGraphTokenContext(); // Get MS Graph token from context
  const [bookings, setBookings] = useState([]);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [selectedBookings, setSelectedBookings] = useState([]);


  // User
  const { accounts } = useMsal();
  const userAccount = accounts[0] || null;
  const user = userAccount ? userAccount.name : "No User";

  // Loading progress bar state
  const [isLoading, setIsLoading] = useState(false);

  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [commentSearchTerm, setCommentSearchTerm] = useState('');
  const [adminSearchTerm, setAdminSearchTerm] = useState('');
  const [deskSearchTerm, setDeskSearchTerm] = useState('');

  const [dateRange, setDateRange] = useState([{
    startDate: new Date(),
    endDate: new Date(),
    key: 'selection'
  }]);

  const monthOptions = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const [filters, setFilters] = useState({
    month: '',
    floor: '',
    area: '',
    desk: '',
    user: '',
    adminUser: '',
    start: formatDateISO(moment(dateRange[0].startDate)),
    end: formatDateISO(moment(dateRange[0].endDate))
  });

  const [floorOptions, setFloorOptions] = useState([]);
  const [areaOptions, setAreaOptions] = useState([]);
  const [filteredAreas, setFilteredAreas] = useState(areaOptions);
  const [filteredFloors, setFilteredFloors] = useState(floorOptions);
  const [isFloorFiltered, setIsFloorFiltered] = useState(false);

  const location = useLocation();
  const initialState = location.state;

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 25
  });

  // Add a state to track window width
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Add useEffect to handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper function to check admin status for every privileged operation
  const verifyAdmin = useCallback(async () => {
    if (!msGraphToken) {
      console.log("Token not available, waiting...");
      return false;
    }

    try {
      const response = await axios.post('/admin', {
        token: msGraphToken,
      });
      return response.status === 200;

    } catch (error) {
      if (error.response && error.response.status === 401) {
        console.log("Admin verification failed, token might be expired");
        await refreshToken();
        return verifyAdmin(); // Retry after refreshing token
      }
      console.error('Error verifying admin status:', error);
      return false;
    }
  }, [msGraphToken, axios, refreshToken]);

  const checkAdminAndSetLoading = useCallback(async () => {
    if (!await verifyAdmin()) {
      setIsLoading(false);
      return false;
    }
    return true;
  }, [verifyAdmin]);


  useEffect(() => {
    setIsLoading(true);
    const fetchData = async () => {  
      try {
        if (await checkAdminAndSetLoading()) {
          axios.get('areas')
            .then(response => {
              if (response.data) {
                let floors = [...new Set(response.data.map(r => r.floor))];
                setFloorOptions(floors);
                setFilteredFloors(floors);
                let areas = response.data.map(r => ({ title: r.title, floor: r.floor }));
                setAreaOptions(areas);
                setFilteredAreas(areas);
              }
            })
            .catch(error => {
              console.error('Error fetching floors:', error);
            });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [axios]);

  useEffect(() => {
    let startDateFormatted = formatDateISO(moment(dateRange[0].startDate));
    let endDate = dateRange[0].endDate ? dateRange[0].endDate : dateRange[0].startDate;
    let endDateFormatted = formatDateISO(moment(endDate));
    setFilters(prev => {
      const newFilters = { ...prev, ['start']: startDateFormatted, ['end']: endDateFormatted };
      return newFilters;
    });
  }, [dateRange]);

  const handleDateRange = (ranges) => {
    setDateRange([ranges.selection]);
  };


  // Extract and apply filters from navigation state on component mount
  useEffect(() => {
    if (initialState) {
      resetFilters();
      const { area, table } = initialState;
      setFilters(prev => ({
        ...prev,
        area: area || '',
        desk: table || '',
      }));
      updateFloorOptions(bookings, "", area);
    }
  }, [initialState]);

  // Create debounced search functions
  const debouncedSearch = useCallback(
    debounce((type, value) => {
      setPagination(prev => ({ ...prev, currentPage: 1 }));
      const valueWithEncodedPeriod = encodeURIComponent(value);
      setFilters(prev => {
        const newFilters = { ...prev, [type]: valueWithEncodedPeriod };
        return newFilters;
      });
    }, 300),
    []
  );


  const handleUserSearch = (e) => {
    const value = e.target.value;
    setUserSearchTerm(value);
    if (value.length >= 3 || value.length === 0) {
      debouncedSearch('user', value);
      handleFilterChange(e, 'user');
    }
  };

  const handleCommentSearch = (e) => {
    const value = e.target.value;
    setCommentSearchTerm(value);
    if (value.length >= 2 || value.length === 0) {
      debouncedSearch('comment', value);
      handleFilterChange(e, 'comment');
    }
  };
  const handleAdminSearch = (e) => {
    const value = e.target.value;
    setAdminSearchTerm(value);
    if (value.length >= 3 || value.length === 0) {
      debouncedSearch('adminUser', value);
      handleFilterChange(e, 'adminUser');
    }
  };

  const handleDeskSearch = (e) => {
    const value = e.target.value;
    setDeskSearchTerm(value);
    if (value.length >= 2 || value.length === 0) {
      debouncedSearch('desk', value);
      handleFilterChange(e, 'desk');
    } 
  };

  useEffect(() => {
    const fetchBookings = async () => {
      setIsLoading(true);
      try {
        const queryParams = new URLSearchParams({
          admin: true,
          page: pagination.currentPage,
          pageSize: pagination.pageSize,
          ...(filters.user && { user: filters.user }),
          ...(filters.comment && { comment: filters.comment }),
          ...(filters.month && { month: filters.month }),
          ...(filters.floor && { floor: filters.floor }),
          ...(filters.area && { area: filters.area }),
          ...(filters.desk && { desk: filters.desk }),
          ...(filters.adminUser && { adminUser: filters.adminUser }),
          ...(filters.start && { start: filters.start }),
          ...(filters.end && { end: filters.end })
        });

        axios.get(`bookings?${queryParams.toString()}`)
          .then(response => {
            if (response.data) {
              setBookings(response.data.bookings);
              setPagination(prev => ({
                ...prev,
                totalPages: response.data.totalPages,
                totalItems: response.data.total,
                currentPage: response.data.currentPage
              }));
            }
          })
          .catch(error => {
            console.error(error);
          })
      } catch (error) {
        console.error('Error fetching bookings:', error);
      } finally {
        setIsLoading(false);
      }
    }
    fetchBookings();
  }, [pagination.currentPage, pagination.pageSize, filters]);

  // Add pagination handler
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
    setSelectedBookings([]); // Clear selections when changing pages
  };



  const handleSelectAll = (e) => {
    setSelectedBookings(e.target.checked ? bookings.map(booking => booking.id) : []);
  };


  // Update for area and desk dropdowns
  const areaDropdownOptions = filteredAreas.map(area =>
    <option key={area.title} value={area.title}>{area.title}</option>
  );

  const resetFilters = () => {
    let startDateFormatted = formatDateISO(moment(dateRange[0].startDate));
    let endDate = dateRange[0].endDate ? dateRange[0].endDate : dateRange[0].startDate;
    let endDateFormatted = formatDateISO(moment(endDate));
    setFilters({
      month: '',
      floor: '',
      area: '',
      desk: '',
      user: '',
      adminUser: '',
      start: startDateFormatted,
      end: endDateFormatted
    });
    setUserSearchTerm('');
    setCommentSearchTerm('');
    setAdminSearchTerm('');
    setDeskSearchTerm('');
    setSelectedBookings([]);
    setFilteredFloors(floorOptions);
    setFilteredAreas(areaOptions);
  };

  const handleFilterChange = (e, filterType) => {
    let value = e.target.value
    const newFilters = { ...filters, [filterType]: value };

    if (filterType === 'month') {
      newFilters.desk = ''; // Reset desk when month or area changes
    } else if (filterType === 'area') {
      newFilters.desk = ''; // Reset desk when month or area changes
      //show only floors on the selected areas
      updateFloorOptions(bookings, "", value);
    } else if (filterType === 'floor') {
      newFilters.desk = ''; // Reset desk when floor changes
      newFilters.area = ''; // Reset area when floor changes
      // If no floor is selected, show all areas
      if (value === "") {
        setFilteredAreas(areaOptions);
      } else {
        setFilteredAreas(areaOptions.filter(area => area.floor === value));  //show only areas of the selected floor
      }
    } else {
      updateFloorOptions(bookings, "", "");   
    }

    setFilters(newFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page when filters change
    setSelectedBookings(selectedBookings.filter(selectedId =>
      bookings.some(booking => booking.id === selectedId)
    ));

  };

  const updateFloorOptions = (bookingsData, selectedMonth, selectedArea) => {
    let restrictedBookings = bookingsData;
    if (selectedMonth) {
      restrictedBookings = restrictedBookings.filter(booking => moment(booking.date).format('MM') === selectedMonth);
    }

    if (selectedArea) {
      restrictedBookings = restrictedBookings.filter(booking => booking.areatitle === selectedArea);
    }

    if (restrictedBookings.length > 0) {
      setFilteredFloors([...new Set(restrictedBookings.map(booking => booking.floor))]);
    } else {
      setFilteredFloors(floorOptions);
    }
    setIsFloorFiltered(true);
  };

  useEffect(() => {
    if (isFloorFiltered) {
      if (filteredFloors.length > 0) {
        setFilteredAreas(areaOptions.filter(area => floorOptions.includes(area.floor)));
      } else {
        setFilteredAreas(areaOptions);
      }
      setIsFloorFiltered(false);
    }
  }, [filteredFloors]);

  // Toggle selection of bookings
  const toggleBooking = (id) => {
    setSelectedBookings(
      selectedBookings.includes(id)
        ? selectedBookings.filter((selectedId) => selectedId !== id)
        : [...selectedBookings, id]
    );
  };

  const [isAdditionalConfirmationModalOpen, setIsAdditionalConfirmationModalOpen] = useState(false);

  const deleteBooking = (booking) => {
    const bookingsToDelete = Array.isArray(booking)
      ? booking
      : booking && typeof booking === 'object' && booking.id
        ? [booking.id]
        : [];

    const visibleSelectedBookings = bookingsToDelete.filter(selectedId =>
      bookings.some(booking => booking.id === selectedId)
    );
    setSelectedBookings(visibleSelectedBookings);

    if (visibleSelectedBookings.length >= 15) {
      setIsAdditionalConfirmationModalOpen(true);
    } else {
      setIsConfirmationModalOpen(true);
    }
  };


  const confirmDeleteBooking = () => {
    if (selectedBookings.length > 0) {
        // Filter selectedBookings to only include IDs that are in the current visible bookings
        const visibleSelectedBookings = selectedBookings.filter(selectedId =>
          bookings.some(booking => booking.id === selectedId)
        );

        // Ensure only bookings visible and selected get deleted
        const deletePromises = visibleSelectedBookings.map(bookingId => {
          if (bookings.map(booking => booking.id).includes(bookingId)) {
            return axios.delete(`bookings/${bookingId}`)
              .catch(() => {
                alert("Something went wrong deleting the booking (403)");
                return Promise.reject();
              });
          }
          return Promise.resolve();
        });

        Promise.all(deletePromises)
          .then(() => {
            // Refresh data after successful deletion
            const queryParams = new URLSearchParams({
              page: pagination.currentPage,
              pageSize: pagination.pageSize,
              ...(filters.user && { user: filters.user }),
              ...(filters.comment && { comment: filters.comment }),
              ...(filters.month && { month: filters.month }),
              ...(filters.floor && { floor: filters.floor }),
              ...(filters.area && { area: filters.area }),
              ...(filters.desk && { desk: filters.desk }),
              ...(filters.adminUser && { adminUser: filters.adminUser }),
              ...(filters.start && { start: filters.start }),
              ...(filters.end && { end: filters.end })
            });

            return axios.get(`bookings?admin=true&${queryParams.toString()}`);
          })
          .then((response) => {
            if (response?.data) {
              setBookings(response.data.bookings);
              setPagination(prev => ({
                ...prev,
                totalPages: response.data.totalPages,
                totalItems: response.data.total,
                currentPage: response.data.currentPage
              }));
              updateFloorOptions(response.data.bookings, filters.month, filters.area);
            }
            setSelectedBookings([]); // Clear selections after deletion
            setIsConfirmationModalOpen(false);
          })
          .catch((error) => {
            console.error('Error refreshing data after deletion:', error);
          });
      }
  };

  const getRowClass = (res) => {
    const isToday = res.date === formatDateISO(moment());
    const isAdmin = res.isAdmin === 1;
    if (isToday && isAdmin) {
      return "active has-background-grey-lighter";
    } else if (isToday) {
      return "active";
    } else if (isAdmin) {
      return "has-background-grey-lighter";
    } else {
      return "";
    }
  };

  // Check if all bookings are selected
  const isAllSelected = () => bookings.length > 0 && selectedBookings.length === bookings.length;

  // Render the data table
  const DataTable = () => {
    return bookings.map((res) => {
      let dateformat = formatDateUI(moment(res.date));
      return (
        <tr key={res.id} className={getRowClass(res)}>
          <td data-label="Select" className="has-text-centered">
            <input
              id={res.id}
              type="checkbox"
              onChange={() => toggleBooking(res.id)}
              checked={selectedBookings.includes(res.id)}
            />
          </td>
          <td data-label="Date">{dateformat}</td>
          <td data-label="User">{res.user}</td>
          <td data-label="Floor" className="has-text-centered">{res.floor}</td>
          <td data-label="Area" className="has-text-centered">{res.areatitle}</td>
          <td data-label="Desk" className="has-text-centered">{res.desktitle}</td>
          <td data-label="Comment by Admin" className={res.comment ? "" : "empty-comment"}>
            {res.comment || " "}
          </td>
          <td data-label="Booked By">{res.bookedBy}</td>
          <td data-label="Actions" className="has-text-centered">
            <Button onClick={() => deleteBooking(res)} size="small" color="danger">
              Delete
            </Button>
          </td>
        </tr>
      );
    });
  };

  const areFiltersApplied = filters.desk || filters.user || filters.comment || filters.adminUser;

  const renderPagination = () => (
    <nav className="pagination is-centered my-4" role="navigation" aria-label="pagination">
      <Button
        className="pagination-previous"
        onClick={() => handlePageChange(pagination.currentPage - 1)}
        disabled={pagination.currentPage === 1}
      >
        Previous
      </Button>
      <Button
        className="pagination-next"
        onClick={() => handlePageChange(pagination.currentPage + 1)}
        disabled={pagination.currentPage === pagination.totalPages}
      >
        Next
      </Button>
      <ul className="pagination-list">
        <li className="pagination-link">
          Page {pagination.currentPage} of {pagination.totalPages}
        </li>
      </ul>
    </nav>
  );

  // Component render
  return (
    <>
      {isLoading && (
        <div className="overlay">
          <div className="spinner"></div>
          <p>Loading. Thank you for your patience...</p>
        </div>
      )}

        <Section>
          <p>{`${user}, these are all admin reservations!`}</p>
          <div style={{ display: 'flex', justifyContent: 'right', gap: '10px' }}>
            <Button className="is-small" style={{ backgroundColor: '#d3d3d3', color: 'black' }}>Admin booking</Button>
        </div>
        <div className="date-range-container">
          <DateRangePicker
            onChange={handleDateRange}
            showSelectionPreview={true}
            moveRangeOnFirstSelection={false}
            months={isMobile ? 1 : 3}
            ranges={dateRange}
            direction={isMobile ? "vertical" : "horizontal"}
            minDate={new Date()}
            maxDate={getMaxDate()}
            staticRanges={[]}
          />
        </div>
      </Section>
        <Section className="table-wrapper">
          <Table striped bordered hoverable className="is-fullwidth">
            <thead>
              <tr className="is-selected">
                <th>
                  <div className="table-header-controls">
                    <Button onClick={() => deleteBooking(selectedBookings)} color="danger">
                      Delete selected
                    </Button>
                    {areFiltersApplied && (
                      <p>
                        <input type="checkbox" onChange={handleSelectAll} checked={isAllSelected()} />
                      </p>
                    )}
                  </div>
                </th>
                <th>
                  <p>
                    <b>Month:</b>
                  </p>
                  <Form.Select
                    value={filters.month}
                    onChange={(e) => handleFilterChange(e, 'month')}
                  >
                    <option value="">All Months</option>
                    {monthOptions.map(month =>
                      <option key={month.value} value={month.value}>{month.label}</option>
                    )}
                  </Form.Select>
                </th>
                <th>
                  <p>
                    <b>User: (min 3 characters)</b>
                  </p>
                  <Form.Input
                    type="text"
                    value={userSearchTerm}
                    onChange={handleUserSearch}
                    placeholder="Search by name or email"
                  />
                </th>
                <th>
                  <p>
                    <b>Floor:</b>
                  </p>
                  <Form.Select
                    value={filters.floor}
                    onChange={(e) => handleFilterChange(e, 'floor')}
                  >
                    <option value="">All Floors</option>
                  {filteredFloors.map(floor =>
                      <option key={floor} value={floor}>{floor}</option>
                    )}
                  </Form.Select>
                </th>
                <th>
                  <p>
                    <b>Area:</b>
                  </p>
                  <Form.Select
                    value={filters.area}
                    onChange={(e) => handleFilterChange(e, 'area')}
                  >
                    <option value="">All Areas</option>
                    {areaDropdownOptions}
                  </Form.Select>
                </th>
                <th>
                  <p>
                    <b>Desk: (min 2 characters)</b>
                  </p>
                  <Form.Input
                    type="text"
                    value={deskSearchTerm}
                    onChange={handleDeskSearch}
                    placeholder="Search Desk"
                  />
                </th>
                <th>Comment by Admin (min 2 characters)
                  <Form.Input
                    type="text"
                    value={commentSearchTerm}
                    onChange={handleCommentSearch}
                    placeholder="Search by comment: "
                  />
                </th>
              <th>Booked by Admin (min 3 characters)
                <Form.Input
                  type="text"
                  value={adminSearchTerm}
                  onChange={handleAdminSearch}
                  placeholder="Search by admin: "
                />
              </th>

                <th>
                  <Button onClick={resetFilters} color="info">
                    Reset Filters
                  </Button>
                </th>
              </tr>
            </thead>
            <tbody>{DataTable()}</tbody>
          </Table>
          {renderPagination()}
          <p className="has-text-centered">
            Showing {bookings.length} of {pagination.totalItems} entries
          </p>
        </Section>
        <DeleteConfirmationComponent
          showConfirmation={isConfirmationModalOpen}
          setShowConfirmation={setIsConfirmationModalOpen}
          onConfirmDelete={confirmDeleteBooking}
          selectedBookings={selectedBookings}
          bookings={bookings}
        />

        <AdditionalConfirmationModal
          show={isAdditionalConfirmationModalOpen}
          selectedBookingsCount={selectedBookings.length}
          onConfirm={() => {
            setIsAdditionalConfirmationModalOpen(false);
            setIsConfirmationModalOpen(true);
          }}
          onCancel={() => setIsAdditionalConfirmationModalOpen(false)}
      />
    </>
  );
};

export default AdminBookingOverviewComponent;
